'use client';

import React from 'react';

import {GwgCarousel} from '@udemy-v2/career-accelerators';
import {TrackImpression, useTracker} from '@udemy-v2/event-tracking';
import {useI18n} from '@udemy-v2/i18n';

import {Heading} from '@/lib/heading-level';

import {useGwgAccelerator} from '../../data/hooks/use-gwg-accelerator';
import {createAcademyPresentedEvent, createAcademySelectedEvent} from '../../data/tracking';
import styles from './gwg-section.module.scss';

const UI_REGION = 'learn-ai-with-google';

export const GwgSection: React.FC = () => {
    const data = useGwgAccelerator();
    const tracker = useTracker();

    if (data === undefined) {
        return <GwgSectionLoadingSkeleton />;
    }
    if (data === null) return null;

    const academySlug = data.slug;

    return (
        <TrackImpression
            trackFunc={() =>
                tracker.publishEvent(
                    createAcademyPresentedEvent({academySlug, uiRegion: UI_REGION}),
                )
            }
            onlyOnce
        >
            <GwgSectionHeaderWrapper>
                <GwgCarousel
                    accelerator={data}
                    onEnrollmentClick={() =>
                        tracker.publishEvent(
                            createAcademySelectedEvent({academySlug, uiRegion: UI_REGION}),
                        )
                    }
                />
            </GwgSectionHeaderWrapper>
        </TrackImpression>
    );
};

export const GwgSectionHeaderWrapper = ({children}: {children: React.ReactNode}) => {
    const headingId = React.useId();
    const {gettext} = useI18n();

    return (
        <section
            data-purpose="gwg-section"
            aria-labelledby={headingId}
            className={styles['gwg-section']}
        >
            <div className={styles['inner-wrapper']}>
                <Heading id={headingId} className={`ud-heading-xxl`}>
                    {gettext('Learn AI with Google')}
                </Heading>
                {children}
            </div>
        </section>
    );
};

/** Pre-composed loading state — used both here (data === undefined) and as LazySection's placeholder in lohp-page.tsx. */
export const GwgSectionLoadingSkeleton: React.FC = () => (
    <GwgSectionHeaderWrapper>
        <div data-purpose="gwg-carousel-skeleton" aria-busy="true" aria-hidden="true">
            <GwgCarousel isLoading />
        </div>
    </GwgSectionHeaderWrapper>
);
